<template>
  <HrPageLayout
    title="Performance Appraisal Reports"
    subtitle="Excel and PDF reports with county letterhead, filtered by cycle and department"
    icon="📑"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Performance Management' }, { label: 'Reports' }]"
  >
    <div class="pr">
      <div class="filters">
        <label>Cycle
          <select v-model.number="cycleId">
            <option :value="0">All cycles</option>
            <option v-for="c in cycles" :key="c.id" :value="Number(c.id)">{{ c.cycle_name }} ({{ c.financial_year }})</option>
          </select>
        </label>
        <label>Department
          <select v-model="department">
            <option value="">All</option>
            <option v-for="d in departments" :key="d" :value="d">{{ d }}</option>
          </select>
        </label>
        <label>Report type
          <select v-model="report">
            <option v-for="r in reportTypes" :key="r.id" :value="r.id">{{ r.label }}</option>
          </select>
        </label>
        <button class="hr-btn hr-btn-primary" :disabled="loading" @click="run">Run report</button>
        <button class="hr-btn hr-btn-outline-success" :disabled="!rows.length || exporting" @click="exportCsv">Excel (CSV)</button>
        <button class="hr-btn hr-btn-outline-primary" :disabled="!rows.length || exporting" @click="exportPdf">PDF (letterhead)</button>
      </div>

      <p class="meta" v-if="generatedAt">Generated: {{ formatDt(generatedAt) }} · Cycle: {{ cycleLabel }} · {{ reportLabel }}</p>

      <div v-if="loading" class="muted pad">Loading…</div>
      <div v-else class="table-wrap">
        <table class="tbl" v-if="rows.length">
          <thead>
            <tr>
              <th v-for="h in headers" :key="h">{{ formatHeader(h) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in rows" :key="i">
              <td v-for="h in headers" :key="h">{{ displayCell(row[h]) }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted pad">Select filters and run a report.</p>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";
import { addCountyLetterhead, loadCountyLogoDataUrl } from "@/utils/payrollReconPdf";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const REPORT_TYPES = [
  { id: "completion", label: "County / departmental completion" },
  { id: "departmental_summary", label: "Departmental performance summary" },
  { id: "not_started", label: "Never started / blank forms" },
  { id: "draft", label: "Draft / incomplete target setting" },
  { id: "targets_rejected", label: "Rejected targets (returned by supervisor)" },
  { id: "pending_employee", label: "Appraisals pending with employees" },
  { id: "pending_supervisor", label: "Appraisals pending with supervisors" },
  { id: "disputed", label: "Disputed appraisals" },
  { id: "variance", label: "Appraisee vs appraiser variance" },
  { id: "rating_distribution", label: "Performance rating distribution" },
  { id: "training_needs", label: "Training-needs analysis" },
  { id: "without_supervisors", label: "Employees without assigned supervisors" }
];

const SKIP_COLS = new Set(["rating_scale_json", "password", "id"]);

export default {
  name: "PasReports",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      exporting: false,
      cycles: [],
      departments: [],
      cycleId: 0,
      department: "",
      report: "completion",
      reportTypes: REPORT_TYPES,
      rows: [],
      cycleMeta: null,
      generatedAt: "",
      letterhead: null
    };
  },
  computed: {
    headers() {
      if (!this.rows.length) return [];
      return Object.keys(this.rows[0]).filter((k) => !SKIP_COLS.has(k) && !String(k).endsWith("_json"));
    },
    reportLabel() {
      return (this.reportTypes.find((r) => r.id === this.report) || {}).label || this.report;
    },
    cycleLabel() {
      if (this.cycleMeta?.cycle_name) return `${this.cycleMeta.cycle_name} (${this.cycleMeta.financial_year || ""})`;
      const c = this.cycles.find((x) => Number(x.id) === Number(this.cycleId));
      return c ? `${c.cycle_name}` : "All cycles";
    }
  },
  mounted() {
    this.loadLookups();
    this.loadLetterhead();
  },
  methods: {
    formatHeader(h) { return String(h).replace(/_/g, " "); },
    formatDt(v) {
      if (!v) return "—";
      try { return new Date(v).toLocaleString(); } catch (_) { return String(v); }
    },
    displayCell(v) {
      if (v == null || v === "") return "—";
      if (typeof v === "object") return JSON.stringify(v);
      return String(v);
    },
    async loadLookups() {
      try {
        const fd = new FormData();
        fd.append("function", "pasListCycles");
        const res = await execute(fd, authUrl);
        this.cycles = res?.data?.data?.cycles || [];
        const active = this.cycles.find((c) => c.status === "active");
        if (active) this.cycleId = Number(active.id);
      } catch (_) { this.cycles = []; }
      try {
        const fd = new FormData();
        fd.append("function", "getDepartments");
        const res = await execute(fd, authUrl);
        this.departments = (res?.data?.data?.departments || []).map((d) => d.departmentName).filter(Boolean).sort();
      } catch (_) { this.departments = []; }
    },
    async loadLetterhead() {
      try {
        const data = new FormData();
        data.append("function", "getCompanyInfo");
        const res = await execute(data, authUrl);
        if (res?.data?.success && res.data.data?.companyInfo) {
          const c = res.data.data.companyInfo;
          const abs = (u) => {
            if (!u) return "";
            if (/^https?:\/\//i.test(u)) return u;
            try { return new URL(u, authUrl).href; } catch (_) { return u; }
          };
          this.letterhead = {
            ...c,
            logoPrimaryUrl: abs(c.logoPrimaryUrl || c.logoPrimary),
            logoSecondaryUrl: abs(c.logoSecondaryUrl || c.logoSecondary)
          };
        }
      } catch (_) {
        this.letterhead = null;
      }
    },
    async run() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "pasGetReportsData");
        fd.append("report", this.report);
        if (this.cycleId) fd.append("cycle_id", String(this.cycleId));
        if (this.department) fd.append("department_name", this.department);
        const res = await execute(fd, authUrl);
        if (!res?.data?.success) {
          toast.error(res?.data?.message || "Report failed");
          this.rows = [];
          return;
        }
        const d = res.data.data || {};
        this.rows = d.rows || [];
        this.cycleMeta = d.cycle || null;
        this.generatedAt = d.generated_at || new Date().toISOString();
        if (!this.rows.length) toast.info("No rows for this report");
      } finally { this.loading = false; }
    },
    exportCsv() {
      if (!this.rows.length || !this.headers.length) return;
      const esc = (v) => {
        const s = this.displayCell(v);
        if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const lines = [
        [this.reportLabel, this.cycleLabel, this.formatDt(this.generatedAt)].map(esc).join(","),
        this.headers.map(esc).join(","),
        ...this.rows.map((r) => this.headers.map((h) => esc(r[h])).join(","))
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pas-${this.report}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV downloaded");
    },
    async exportPdf() {
      if (!this.rows.length) return;
      this.exporting = true;
      try {
        if (!this.letterhead) await this.loadLetterhead();
        const doc = new jsPDF({ orientation: this.headers.length > 6 ? "landscape" : "portrait", unit: "mm", format: "a4" });
        const logoDataUrl = await loadCountyLogoDataUrl(this.letterhead);
        const startY = addCountyLetterhead(doc, {
          title: "PERFORMANCE APPRAISAL REPORT",
          subtitle: this.reportLabel,
          periodLabel: `${this.cycleLabel}${this.department ? " · " + this.department : ""}`,
          logoDataUrl,
          letterhead: this.letterhead
        });
        autoTable(doc, {
          startY: startY + 4,
          head: [this.headers.map((h) => this.formatHeader(h))],
          body: this.rows.map((r) => this.headers.map((h) => this.displayCell(r[h]))),
          styles: { fontSize: 7, cellPadding: 1.5 },
          headStyles: { fillColor: [12, 74, 110] }
        });
        doc.save(`pas-${this.report}-${Date.now()}.pdf`);
        toast.success("PDF downloaded");
      } catch (e) {
        toast.error(e?.message || "PDF export failed");
      } finally {
        this.exporting = false;
      }
    }
  }
};
</script>

<style scoped>
.filters { display:flex; flex-wrap:wrap; gap:.65rem; align-items:flex-end; margin-bottom:1rem; }
.filters label { display:flex; flex-direction:column; gap:.3rem; font-size:.75rem; font-weight:700; color:#475569; }
.filters select { border:1px solid #e2e8f0; border-radius:8px; padding:.45rem .65rem; min-width:180px; background:#fff; }
.meta { font-size:.85rem; color:#64748b; margin:0 0 .75rem; }
.table-wrap { background:#fff; border:1px solid #e2e8f0; border-radius:12px; overflow:auto; }
.tbl { width:100%; border-collapse:collapse; font-size:.82rem; }
.tbl th, .tbl td { padding:.55rem .7rem; border-bottom:1px solid #f1f5f9; text-align:left; white-space:nowrap; }
.tbl th { background:#f8fafc; text-transform:capitalize; color:#64748b; font-size:.72rem; }
.muted { color:#94a3b8; }
.pad { padding:1.5rem; }
</style>
